import axios from "axios";
import moment from "moment";
import axiosAuth from "../../utilities/interceptor";
import { loadScript } from "../../utilities";
import { toast } from "react-toastify";
import utsavPackLogo from "../../assets/images/utsav-pack-logo.png";

export function calculateTotalBill(
  cartList,
  applyDiscount,
  deliveryFeeData,
  deliveryType,
) {
  let gstSlab = [];
  let cakeTotalAmount = 0;
  let addonTotalAmount = 0;

  // ✅ Filter products based on delivery type
  const productsBasedOnDeliveryType = (cartList?.data?.details || []).filter(
    (item) => {
      if (deliveryType === "DOOR_STEP_DELIVERY") {
        if (item?.parent_id && item?.parent_product) {
          return item?.parent_product?.product_tags?.door_step_delivery === 1;
        }
        return item?.products?.product_tags?.door_step_delivery === 1;
      }
      if (deliveryType === "PICK_UP") {
        if (item?.parent_id && item?.parent_product) {
          return item?.parent_product?.product_tags?.pick_up === 1;
        }
        return item?.products?.product_tags?.pick_up === 1;
      }
      return true;
    },
  );

  // ✅ Normal product subtotal using ORIGINAL price (for coupon eligibility & comparison base)
  const normalProductSubTotal = productsBasedOnDeliveryType.reduce(
    (sum, cart) => {
      if (cart?.parent_id > 0) return sum; // skip combos
      if (cart?.is_bulk_product == 0) {
        // Use original price (before product discount) as the base
        return sum + +cart.product_price * +cart.product_quantity;
      }
      return sum;
    },
    0,
  );

  // ✅ Combo subtotal (counted once per parent_id)
  const countedCombosForSubTotal = new Set();
  const comboSubTotal = productsBasedOnDeliveryType.reduce((sum, cart) => {
    if (cart?.parent_id > 0 && cart?.parent_product?.combo_price) {
      if (!countedCombosForSubTotal.has(cart.parent_id)) {
        countedCombosForSubTotal.add(cart.parent_id);
        return sum + +cart.parent_product.combo_price * +cart.product_quantity;
      }
    }
    return sum;
  }, 0);

  // ✅ cartSubTotal for display = normal (discounted) + combo
  const normalProductDiscountedSubTotal = productsBasedOnDeliveryType.reduce(
    (sum, cart) => {
      if (cart?.parent_id > 0) return sum;
      if (cart?.is_bulk_product == 0) {
        return sum + +cart.product_discount_price * +cart.product_quantity;
      }
      return sum;
    },
    0,
  );
  const dynamicCouponSave = applyDiscount?.code
    ? parseFloat(
        calculateDiscountAmount(
          applyDiscount?.percentage,
          applyDiscount?.type,
          normalProductSubTotal, // updates when qty changes
        ),
      )
    : 0;
  // const cartSubTotal = normalProductDiscountedSubTotal + comboSubTotal;
  // const cartSubTotal = normalProductSubTotal + comboSubTotal;
  //
  const cartSubTotal = normalProductSubTotal + comboSubTotal;
  const productDiscountSave =
    normalProductSubTotal - normalProductDiscountedSubTotal;
  // ✅ ADD THESE
  const normalItems = productsBasedOnDeliveryType.filter(
    (item) => item?.is_bulk_product == 0 && !(item?.parent_id > 0),
  );
  const normalItemCount = normalItems.length;

  // Per item — calculate effective display amounts
  const effectiveProductDiscountSave = normalItems.reduce((sum, cart) => {
    const originalPrice = +cart.product_price * +cart.product_quantity;
    const discountedPrice =
      +cart.product_discount_price * +cart.product_quantity;
    const productSaving = originalPrice - discountedPrice;
    let couponSaving = 0;
    if (applyDiscount?.code) {
      if (applyDiscount?.type === "PERCENTAGE") {
        couponSaving =
          originalPrice * (parseFloat(applyDiscount.percentage) / 100);
      } else {
        couponSaving =
          normalItemCount > 0 ? dynamicCouponSave / normalItemCount : 0;
      }
    }
    return productSaving > couponSaving ? sum + productSaving : sum;
  }, 0);

  const effectiveCouponSave = normalItems.reduce((sum, cart) => {
    const originalPrice = +cart.product_price * +cart.product_quantity;
    const discountedPrice =
      +cart.product_discount_price * +cart.product_quantity;
    const productSaving = originalPrice - discountedPrice;
    let couponSaving = 0;
    if (applyDiscount?.code) {
      if (applyDiscount?.type === "PERCENTAGE") {
        couponSaving =
          originalPrice * (parseFloat(applyDiscount.percentage) / 100);
      } else {
        couponSaving =
          normalItemCount > 0 ? dynamicCouponSave / normalItemCount : 0;
      }
    }
    return couponSaving >= productSaving ? sum + couponSaving : sum;
  }, 0);
  const bulkProductSubTotal = productsBasedOnDeliveryType?.reduce(
    (sum, cart) => {
      if (cart?.is_bulk_product > 0) {
        return sum + +cart.product_discount_price * +cart.product_quantity;
      }
      return sum;
    },
    0,
  );

  // ✅ Discount applied ONLY on normal products (not combos, not bulk)
  // const cakeSubTotalAfterDiscount =
  //   normalProductDiscountedSubTotal - (applyDiscount?.save || 0);
  const cakeSubTotalAfterDiscount =
    normalProductDiscountedSubTotal - dynamicCouponSave;

  const accumulateGstSlab = (taxPercentage, gst) => {
    if (taxPercentage) {
      const existingSlab = gstSlab.find(
        (slab) => slab.taxPercentage === taxPercentage,
      );
      const cgst = gst / 2;
      const sgst = gst / 2;
      if (existingSlab) {
        existingSlab.cgst += cgst;
        existingSlab.sgst += sgst;
        existingSlab.gst += gst;
      } else {
        gstSlab.push({ taxPercentage, cgst, sgst, gst });
      }
    }
  };

  const calculateDetails = (items, applyProductDiscount = false) => {
    // Group combo children by parent_id
    const comboGroups = {};
    items.forEach((item) => {
      if (item.parent_id > 0 && item.parent_product?.combo_price) {
        if (!comboGroups[item.parent_id]) comboGroups[item.parent_id] = [];
        comboGroups[item.parent_id].push(item);
      }
    });

    // Calculate savings per combo child
    const comboSavingsMap = {};
    Object.entries(comboGroups).forEach(([parentId, groupItems]) => {
      const originalComboProductPrice = groupItems?.reduce(
        (total, item) =>
          total +
          (+item?.variations?.egg > 0
            ? +item?.variations?.egg
            : +item?.variations?.eggLess || 0),
        0,
      );
      const originalPrice =
        +originalComboProductPrice * groupItems[0]?.product_quantity;

      const comboPrice =
        +groupItems[0]?.parent_product?.combo_price *
        +groupItems[0]?.product_quantity;
      const totalSavings = originalPrice - comboPrice;
      const perChildSavings = totalSavings / groupItems.length;
      groupItems.forEach((item) => {
        comboSavingsMap[item.cart_id] = perChildSavings;
      });
    });

    // ✅ Count only normal (non-combo, non-bulk) items for flat discount split
    const normalItemCount = items.filter(
      (item) => item?.is_bulk_product == 0 && !(item?.parent_id > 0),
    ).length;

    return items.reduce(
      (accumulator, item) => {
        let price = 0;
        let priceForQty; // This will be product_discount_price * qty for normal items

        if (item?.parent_id > 0) {
          // Combo child: use original child price
          priceForQty =
            +item?.variations?.egg > 0
              ? +item?.variations?.egg
              : +item?.variations?.eggLess || 0;
          priceForQty =
            priceForQty * parseInt(item.product_quantity || item.quantity);
        } else {
          // Normal product: product_discount_price (already has product-level discount)
          priceForQty =
            parseFloat(item.product_discount_price || item.price) *
            parseInt(item.product_quantity || item.quantity);
        }

        if (item.parent_id > 0 && item.parent_product?.combo_price) {
          // ✅ Combo child: subtract combo savings (NO coupon discount)
          const savings = comboSavingsMap[item.cart_id] || 0;
          price = priceForQty - savings;
        } else if (
          applyProductDiscount &&
          item?.is_bulk_product == 0 &&
          item.parent_id <= 0
        ) {
          // ✅ Normal product: Compare product discount vs coupon discount — take HIGHEST

          // Original price before any discount
          const originalPriceForItem =
            parseFloat(item.product_price || item.price) *
            parseInt(item.product_quantity || item.quantity);

          // Saving from product's own discount (product_price - product_discount_price)
          const productDiscountSaving = originalPriceForItem - priceForQty;

          // Saving from coupon applied on original price
          let couponSavingForItem = 0;
          if (applyDiscount?.code) {
            if (applyDiscount?.type === "PERCENTAGE") {
              // Percentage coupon: apply on original price per item
              couponSavingForItem = parseFloat(
                calculateDiscountAmount(
                  applyDiscount?.percentage,
                  applyDiscount?.type,
                  originalPriceForItem,
                ),
              );
            } else {
              // Flat coupon: split equally across all normal items
              // couponSavingForItem =
              //   normalItemCount > 0
              //     ? (applyDiscount?.save || 0) / normalItemCount
              //     : 0;
              couponSavingForItem =
                normalItemCount > 0 ? dynamicCouponSave / normalItemCount : 0;
            }
          }

          // ✅ Take whichever gives MORE saving to the customer
          if (couponSavingForItem > productDiscountSaving) {
            // Coupon wins → apply coupon on original price
            price = originalPriceForItem - couponSavingForItem;
          } else {
            // Product discount wins → keep product_discount_price as is
            price = priceForQty;
          }
        } else {
          // Bulk product or addon — no coupon discount
          price = priceForQty;
        }

        const taxPercentage = parseFloat(
          item?.products?.tax?.tax_percentage || item?.tax?.tax_percentage || 0,
        );
        const percentToDivide = (100 + parseInt(taxPercentage)) / 100;
        const taxAmount = price / percentToDivide;

        const gstAmount = taxAmount * (taxPercentage / 100);
        const totalPriceIncludingTax = taxAmount + gstAmount;
        accumulateGstSlab(taxPercentage, gstAmount);

        accumulator.totalAmount += parseFloat(
          totalPriceIncludingTax.toFixed(2),
        );
        accumulator.taxableAmount += parseFloat(taxAmount.toFixed(2));
        accumulator.gstAmount += parseFloat(gstAmount.toFixed(2));
        return accumulator;
      },
      { totalAmount: 0, taxableAmount: 0, gstAmount: 0 },
    );
  };

  let {
    totalAmount: finalTotalAmount,
    taxableAmount: finalTaxableAmount,
    gstAmount: finalGstAmount,
  } = { totalAmount: 0, taxableAmount: 0, gstAmount: 0 };

  if (productsBasedOnDeliveryType?.length > 0) {
    const { totalAmount, taxableAmount, gstAmount } = calculateDetails(
      productsBasedOnDeliveryType,
      true,
    );
    finalTotalAmount += totalAmount;
    finalTaxableAmount += taxableAmount;
    finalGstAmount += gstAmount;
    cakeTotalAmount = totalAmount;
  }

  if (cartList?.data?.addons?.length > 0) {
    const { totalAmount, taxableAmount, gstAmount } = calculateDetails(
      cartList.data.addons,
    );
    finalTotalAmount += totalAmount;
    finalTaxableAmount += taxableAmount;
    finalGstAmount += gstAmount;
    addonTotalAmount = totalAmount;
  }

  if (deliveryFeeData?.rate > 0 && deliveryType === "DOOR_STEP_DELIVERY") {
    finalTotalAmount += deliveryFeeData?.rate || 0;
    finalTaxableAmount += deliveryFeeData?.rate_without_gst || 0;
    finalGstAmount += deliveryFeeData?.rate_gst_total || 0;
    accumulateGstSlab(
      deliveryFeeData?.rate_gst_percentage,
      deliveryFeeData?.rate_gst_total,
    );
  }

  gstSlab = gstSlab.sort((a, b) => a.taxPercentage - b.taxPercentage);
  const gstSlabResult = gstSlab.map((slab) => ({
    taxPercentage: slab.taxPercentage,
    cgst: slab.cgst.toFixed(2),
    sgst: slab.sgst.toFixed(2),
    gst: slab.gst.toFixed(2),
    cgstPercent: slab?.taxPercentage / 2,
    sgstPercent: slab?.taxPercentage / 2,
  }));

  return {
    totalAmount: finalTotalAmount.toFixed(2),
    taxableAmount: (+finalTotalAmount - +finalGstAmount).toFixed(2),
    gstAmount: finalGstAmount.toFixed(2),
    cakeSubTotalAfterDiscount: cakeSubTotalAfterDiscount,
    addonTotalAmount: addonTotalAmount,
    cakeTotalAmount: cakeTotalAmount,
    gstSlab: gstSlabResult,
    cartSubTotal: cartSubTotal,
    bulkSubTotal: bulkProductSubTotal,
    normalProductSubTotal: normalProductSubTotal,
    // dynamicCouponSave: dynamicCouponSave,
    // productDiscountSave: productDiscountSave,
    // productDiscountSave: showProductDiscount ? productDiscountSave : 0, // ✅
    // dynamicCouponSave: showCouponDiscount ? dynamicCouponSave : 0,
    productDiscountSave: effectiveProductDiscountSave,
    dynamicCouponSave: effectiveCouponSave,
    rawCouponSave: dynamicCouponSave,
  };
}

export function concatAddress(item) {
  if (Object.keys(item).length === 0) {
    return "";
  }
  if (item?.full_address) {
    return item?.full_address;
  }
  return `${item?.doorNo}, ${item?.street}, ${item?.area}, ${item?.address_city_district}, ${item?.address_state}`;
}

export const reorderAddresses = (addresses, primaryAddressId) => {
  if (!Array.isArray(addresses)) return [];

  const primaryAddress = addresses.find(
    (addr) => addr.customer_address_id === primaryAddressId,
  );
  const remainingAddresses = addresses.filter(
    (addr) => addr.customer_address_id !== primaryAddressId,
  );

  return primaryAddress ? [primaryAddress, ...remainingAddresses] : addresses;
};

export const getMoment = (date, format = null) => {
  let DEFAULT_FORMAT = "YYYY-MM-DD";

  if (format) {
    DEFAULT_FORMAT = format;
  }
  const currentDate = moment(date);
  const parsedDate = currentDate.format(DEFAULT_FORMAT);
  return parsedDate;
};

export const calculateDiscountAmount = (percent, type, cartSubTotal) => {
  const percentage = parseFloat(percent);
  const subTotal = parseFloat(cartSubTotal);

  if (isNaN(percentage)) return "0";
  if (type === "PERCENTAGE" && isNaN(subTotal)) return "0";

  let amount;

  if (type === "PERCENTAGE") {
    amount = subTotal * (percentage / 100);
  } else {
    amount = percentage;
  }

  const roundedAmount = Math.round(amount * 100) / 100;

  return roundedAmount % 1 === 0
    ? roundedAmount.toFixed(0)
    : roundedAmount.toFixed(2);
};

export async function displayRazorpay() {
  const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

  if (!res) {
    alert("Razorpay SDK failed to load. Are you online?");
    return;
  }

  try {
    const { data } = await axiosAuth.post("http://localhost:1769/razorpay");

    const options = {
      key: "rzp_test_6GAyRY1AX2udgz",
      amount: data.amount,
      currency: data.currency,
      name: "MellowFellow",
      description: "Test Transaction",
      image: "https://picsum.photos/200/300.jpg",
      order_id: data.id,
      handler: function (response) {
        alert(
          `Payment successful! Payment ID: ${response.razorpay_payment_id}`,
        );
      },
      prefill: {
        name: "Your Name",
        email: "your-email@example.com",
        contact: "9999999999",
      },
      theme: {
        color: "#3399cc",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  } catch (error) {
    console.error("Error in payment initialization:", error);
    alert("Something went wrong. Please try again.");
  }
}

export const triggerRazorPay = async (payload, orderId, user) => {
  const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

  if (!res) {
    alert("Razorpay SDK failed to load. Are you online?");
    return null;
  }

  if (!user?.PAYMENT_KEY) {
    toast.error("Payment Initialization failed");
    return null;
  }

  return new Promise((resolve, reject) => {
    const options = {
      description: "Packaging | Printing | Possibilities",
      image: utsavPackLogo,
      currency: "INR",
      key: user?.PAYMENT_KEY,
      name: "Utsav Pack",
      order_id: orderId,
      prefill: {
        contact: user?.customer_mobile,
        name: user?.customer_name,
      },
      theme: { color: "#1D1B1B" },
      handler: function (response) {
        resolve(response.razorpay_payment_id);
      },
      modal: {
        ondismiss: function () {
          reject({ error: "Payment popup closed by user" });

          const cancelledPayload = {
            message: "Payment popup closed by user",
            checkout_tracking_id: payload?.checkout_tracking_id,
          };
          orderStatusCancelUpdate(cancelledPayload);
        },
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.open();

    rzp1.on("payment.failed", async function (response) {
      const cancelledPayload = {
        message: response.error.description,
        checkout_tracking_id: payload?.checkout_tracking_id,
      };
      await orderStatusCancelUpdate(cancelledPayload);
      reject(response.error);
    });
  });
};

export const processOrderBackup = async (requestBody) => {
  try {
    if (requestBody) {
      const response = await axiosAuth.post(
        "processOrderBackupSubmit",
        requestBody,
      );
      return response?.data;
    }
  } catch (error) {
    toast.error(error?.message);
    return error;
  }
};

export const successOrder = async (requestBody) => {
  try {
    if (requestBody) {
      const response = await axiosAuth.post("submitOrder", requestBody);
      return response?.data;
    }
  } catch (error) {
    return error;
  }
};

export const generateOrderId = async (url, requestBody) => {
  try {
    if (requestBody) {
      const response = await axiosAuth.post(url, requestBody);
      return response?.data?.data?.order_id;
    }
  } catch (error) {
    return -1;
  }
};

export const orderStatusCancelUpdate = async (requestBody) => {
  try {
    if (requestBody) {
      const response = await axiosAuth.post(
        "orderStatusCancelUpdate",
        requestBody,
      );
      return response?.data;
    }
  } catch (error) {
    return error;
  }
};

export function generateRandomNumber() {
  const currentDateTime = moment().format("YYYYMMDDHHmmssSSS");
  return currentDateTime.toString();
}

export function areProductSetsEqual(setA, setB) {
  if (setA?.size !== setB?.size) return false;
  for (let id of setA) {
    if (!setB?.has(id)) return false;
  }
  return true;
}

export const getCartDisplayCount = (products) => {
  const parentIds = new Set();
  let count = 0;
  products.forEach((item) => {
    if (item?.parent_id) {
      if (!parentIds.has(item.parent_id)) {
        parentIds.add(item.parent_id);
        count += 1;
      }
    } else {
      count += 1;
    }
  });
  return count;
};
